import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const imageSets = [
  {
    src: "/images/gallery/hero11.jpg",
    alt: "Infinity pool with Atlas Mountain views at Laurier Rose Villa",
  },
  {
    src: "/images/gallery/herov.jpg",
    alt: "Sunset over Ourika Valley from the villa terrace",
  },
  {
    src: "/images/gallery/hero33.jpg",
    alt: "Luxury interior and living spaces of the villa",
  },
];

const SLIDE_INTERVAL = 8000;

const Hero = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const intervalRef = useRef(null);
  const { t } = useTranslation();

  // Preload images 2 and 3 after mount
  useEffect(() => {
    imageSets.slice(1).forEach((image) => {
      const img = new Image();
      img.src = image.src;
    });
  }, []);

  // Slideshow timer
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % imageSets.length);
    }, SLIDE_INTERVAL);
    return () => clearInterval(intervalRef.current);
  }, []);

  const navigateImage = (index) => {
    if (index === currentIndex) return;
    setCurrentIndex(index);
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % imageSets.length);
    }, SLIDE_INTERVAL);
  };

  const scrollToContent = () => {
    window.scrollTo({ top: window.innerHeight, behavior: "smooth" });
  };

  return (
    <section className="relative w-full h-screen flex items-center justify-center overflow-hidden bg-ink">
      {/* Background slideshow */}
      <div className="absolute inset-0 z-0">
        <AnimatePresence>
          <motion.div
            key={currentIndex}
            className="absolute inset-0 w-full h-full"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.6, ease: "easeInOut" }}
          >
            <motion.img
              src={imageSets[currentIndex].src}
              alt={imageSets[currentIndex].alt}
              className="w-full h-full object-cover"
              initial={{ scale: 1.05 }}
              animate={{ scale: 1.14 }}
              transition={{ duration: SLIDE_INTERVAL / 1000 + 2, ease: "linear" }}
              loading={currentIndex === 0 ? "eager" : "lazy"}
              fetchpriority={currentIndex === 0 ? "high" : "low"}
              decoding={currentIndex === 0 ? "sync" : "async"}
            />
          </motion.div>
        </AnimatePresence>

        {/* Cinematic overlays for legibility */}
        <div className="absolute inset-0 bg-ink/30" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/40 via-ink/10 to-ink/70" />
      </div>

      {/* Content */}
      <div className="relative z-10 text-center text-white px-6 w-full max-w-4xl mx-auto">
        <motion.div
          className="eyebrow text-white/75 mb-6 flex items-center justify-center gap-4"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <span className="w-8 h-px bg-brass-light/70" />
          <span>Laurier Rose · Ourika Valley</span>
          <span className="w-8 h-px bg-brass-light/70" />
        </motion.div>

        <motion.h1
          className="font-serif font-light text-5xl md:text-6xl lg:text-7xl leading-[1.08] tracking-wide mb-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.35 }}
        >
          {t("hero.title")}
        </motion.h1>

        <motion.div
          className="w-20 h-px bg-brass-light mx-auto mb-8"
          initial={{ width: 0, opacity: 0 }}
          animate={{ width: 80, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        />

        <motion.p
          className="text-base md:text-lg font-light tracking-wide text-white/85 max-w-2xl mx-auto mb-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.75 }}
        >
          {t("hero.subtitle")}
        </motion.p>

        <motion.div
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.95 }}
        >
          <Link
            to="/booking"
            className="inline-flex items-center justify-center bg-brass hover:bg-brass-dark text-ink text-[12px] uppercase tracking-[0.22em] py-4 px-10 transition-colors duration-300 w-full sm:w-auto"
          >
            {t("hero.bookNow")}
          </Link>
          <Link
            to="/overview"
            className="inline-flex items-center justify-center border border-white/60 text-white hover:bg-white hover:text-ink text-[12px] uppercase tracking-[0.22em] py-4 px-10 transition-all duration-300 w-full sm:w-auto"
          >
            {t("hero.exploreVilla")}
          </Link>
        </motion.div>
      </div>

      {/* Indicators + scroll hint */}
      <div className="absolute bottom-8 left-0 right-0 z-10">
        <div className="flex justify-center items-center mb-6">
          <div className="flex space-x-3">
            {imageSets.map((_, index) => (
              <button
                key={index}
                onClick={() => navigateImage(index)}
                aria-label={`Slide ${index + 1}`}
                className={`h-px transition-all duration-500 ${
                  index === currentIndex
                    ? "w-10 bg-brass-light"
                    : "w-5 bg-white/40 hover:bg-white/70"
                }`}
              />
            ))}
          </div>
        </div>

        <motion.button
          className="flex flex-col items-center gap-2 mx-auto cursor-pointer"
          onClick={scrollToContent}
          aria-label="Scroll to content"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.2 }}
        >
          <span className="eyebrow text-white/60 text-[10px]">Explore</span>
          <motion.span
            animate={{ y: [0, 6, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          >
            <svg
              className="w-4 h-4 text-white/60"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M19 14l-7 7m0 0l-7-7m7 7V3"
              />
            </svg>
          </motion.span>
        </motion.button>
      </div>
    </section>
  );
};

export default Hero;
